import { classifyProductImages, enhanceExtractedImage, extractProductImages, locateImageCandidates, slugify, verifyExtractedImage } from "./imageCreationService";
export { extractProductImages, locateImageCandidates, enhanceExtractedImage } from "./imageCreationService";

export const EXTRACTION_MAX_PAGES = 20;
export const EXTRACTION_PDF_MAX_BYTES = 25 * 1024 * 1024;

const PAGE_WIDTH_PX = 1600; // fallback page render width (scanned/flat PDFs only)
const THUMB_PX = 512; // candidate thumbnails sent to the AI
const MAX_CANDIDATES = 80; // pictures shown to the AI when looking for named products (a busy flyer has many)
const MIN_AREA = 0.002; // smaller than 0.2% of the page = icon/badge, not a product photo (a busy flyer has many small packshots)
const MIN_SIDE = 0.03; // thinner than 3% of the page width/height = a rule or strip, not a product photo
const MAX_AREA = 0.8; // bigger = a whole scanned page, handled by the fallback
const VERIFY_PX = 1024; // a chosen picture is checked at this size (the AI first only sees THUMB_PX previews)
const MAX_ALTERNATES = 3; // other pictures tried (and verified) for a product whose first pick was wrong

const loadPdf = async (file) => {
  const pdfjsLib = await import("pdfjs-dist");
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
  const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
  if (pdf.numPages > EXTRACTION_MAX_PAGES) {
    throw new Error(`This PDF has ${pdf.numPages} pages. Please use a PDF with ${EXTRACTION_MAX_PAGES} pages or fewer.`);
  }
  return { pdfjsLib, pdf };
};

/* ------------------------------------------------------------------ */
/* Fallback: render whole pages (scanned / flattened PDFs)              */
/* ------------------------------------------------------------------ */

/** Renders PDF pages to JPEG Blobs. onProgress(done, total). */
export const renderPdfPages = async (file, { onProgress } = {}) => {
  const { pdf } = await loadPdf(file);
  const pages = [];
  for (let n = 1; n <= pdf.numPages; n++) {
    const page = await pdf.getPage(n);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: Math.min(4, PAGE_WIDTH_PX / base.width) });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88));
    canvas.width = canvas.height = 0; // release memory
    if (blob) pages.push(blob);
    onProgress?.(n, pdf.numPages);
  }
  return pages;
};

/* ------------------------------------------------------------------ */
/* Fast path: the pictures embedded in the PDF                          */
/* ------------------------------------------------------------------ */
// Product photos in a catalogue are normally separate images placed on the page.
// We list those, show the AI small thumbnails (+ nearby text) to pick the right
// one, and then work on the real embedded image at its native resolution.

const mulMatrix = (m, n) => [
  m[0] * n[0] + m[2] * n[1],
  m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3],
  m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4],
  m[1] * n[4] + m[3] * n[5] + m[5],
];

/**
 * Every image painted on a page with its normalised placement (0-1, top-left origin).
 * Pure with respect to the DOM so it can be unit-tested in Node.
 * @returns {Promise<{id: string|null, inline: object|null, x:number,y:number,w:number,h:number}[]>}
 */
export const collectImagePlacements = async (pdfjsLib, page) => {
  const { OPS } = pdfjsLib;
  const list = await page.getOperatorList();
  const viewport = page.getViewport({ scale: 1 });
  let ctm = [1, 0, 0, 1, 0, 0];
  const stack = [];
  const placements = [];

  const place = (id, inline) => {
    const pts = [
      [0, 0],
      [1, 0],
      [0, 1],
      [1, 1],
    ].map(([x, y]) => viewport.convertToViewportPoint(ctm[0] * x + ctm[2] * y + ctm[4], ctm[1] * x + ctm[3] * y + ctm[5]));
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    placements.push({
      id,
      inline,
      x: Math.min(...xs) / viewport.width,
      y: Math.min(...ys) / viewport.height,
      w: (Math.max(...xs) - Math.min(...xs)) / viewport.width,
      h: (Math.max(...ys) - Math.min(...ys)) / viewport.height,
    });
  };

  for (let i = 0; i < list.fnArray.length; i++) {
    const fn = list.fnArray[i];
    const args = list.argsArray[i];
    if (fn === OPS.save) stack.push(ctm);
    else if (fn === OPS.restore) ctm = stack.pop() || ctm;
    else if (fn === OPS.transform) ctm = mulMatrix(ctm, args);
    else if (fn === OPS.paintFormXObjectBegin) {
      stack.push(ctm);
      if (Array.isArray(args?.[0])) ctm = mulMatrix(ctm, args[0]);
    } else if (fn === OPS.paintFormXObjectEnd) ctm = stack.pop() || ctm;
    else if (fn === OPS.paintImageXObject || fn === OPS.paintJpegXObject) place(args[0], null);
    else if (fn === OPS.paintInlineImageXObject) place(null, args[0]);
  }
  return placements;
};

const getPdfObject = (page, id) =>
  new Promise((resolve) => {
    const store = String(id).startsWith("g_") ? page.commonObjs : page.objs;
    const timer = setTimeout(() => resolve(null), 4000);
    const done = (v) => {
      clearTimeout(timer);
      resolve(v || null);
    };
    try {
      const ready = store.get(id);
      if (ready) return done(ready);
    } catch {
      /* not resolved yet — wait for it below */
    }
    store.get(id, done);
  });

/** Decoded PDF image data → canvas at the image's native pixel size (or null if unsupported). */
const imageDataToCanvas = (img) => {
  const { width, height } = img || {};
  if (!width || !height) return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (img.bitmap) {
    ctx.drawImage(img.bitmap, 0, 0);
    return canvas;
  }
  const d = img.data;
  if (!d) return null;
  const px = width * height;
  const rgba = new Uint8ClampedArray(px * 4);
  if (d.length === px * 4) rgba.set(d);
  else if (d.length === px * 3) {
    for (let i = 0, j = 0, k = 0; i < px; i++, k += 3, j += 4) {
      rgba[j] = d[k];
      rgba[j + 1] = d[k + 1];
      rgba[j + 2] = d[k + 2];
      rgba[j + 3] = 255;
    }
  } else if (d.length === px) {
    for (let i = 0, j = 0; i < px; i++, j += 4) {
      rgba[j] = rgba[j + 1] = rgba[j + 2] = d[i];
      rgba[j + 3] = 255;
    }
  } else return null; // 1-bit masks etc.
  ctx.putImageData(new ImageData(rgba, width, height), 0, 0);
  return canvas;
};

const toBlob = (canvas, type, quality) => new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const decodeCandidate = async (c) => {
  const img = c.placement.inline || (await getPdfObject(c.page, c.placement.id));
  return imageDataToCanvas(img);
};

/** Small JPEG preview for the AI (transparent areas on white). */
const makeThumb = async (canvas) => {
  const scale = Math.min(1, THUMB_PX / Math.max(canvas.width, canvas.height));
  const t = document.createElement("canvas");
  t.width = Math.max(1, Math.round(canvas.width * scale));
  t.height = Math.max(1, Math.round(canvas.height * scale));
  const ctx = t.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, t.width, t.height);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(canvas, 0, 0, t.width, t.height);
  return toBlob(t, "image/jpeg", 0.8);
};

/** Larger JPEG (VERIFY_PX) of the real picture, for the "is this really the product?" check. */
const makeVerifyBlob = async (canvas) => {
  const scale = Math.min(1, VERIFY_PX / Math.max(canvas.width, canvas.height));
  const t = document.createElement("canvas");
  t.width = Math.max(1, Math.round(canvas.width * scale));
  t.height = Math.max(1, Math.round(canvas.height * scale));
  const ctx = t.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, t.width, t.height);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(canvas, 0, 0, t.width, t.height);
  const blob = await toBlob(t, "image/jpeg", 0.88);
  t.width = t.height = 0;
  return blob;
};

/* ------------------------------------------------------------------ */
/* Matching the product name against the text printed in the PDF         */
/* ------------------------------------------------------------------ */
// The customer types a product name; if that name is printed in the PDF (a caption,
// a headline, a price tag), the picture next to it is the one to extract. This is
// used as a strong hint for the AI and as a fallback when the AI isn't sure.

const STOP_WORDS = new Set(["the", "a", "an", "of", "for", "and", "or", "to", "my", "our"]);

/** "Extract product image for Rico Rice from the attached PDF and enhance it" → "Rico Rice". */
export const productNameFrom = (prompt) => {
  const original = String(prompt || "").trim();
  let s = original;
  const m = s.match(/\b(?:image|picture|photo|pic|img)s?\s+(?:of|for)\s+(.+)/i);
  if (m) s = m[1];
  else s = s.replace(/^(?:please\s+)?(?:extract|get|find|download|show|give\s+me|pull)\s+(?:out\s+)?(?:the\s+)?/i, "");
  s = s.replace(/\s+(?:from|out of|inside|in (?:the|this|attached|my) pdf|and (?:enhance|make|extract|download|give|then)|then|with (?:a|sharper|clean))\b.*$/i, "");
  s = s.replace(/[.,;:!?]+$/, "").trim();
  return s || original;
};

/**
 * Every product the customer named: "Rico Rice and Tata Salt" → ["Rico Rice", "Tata Salt"].
 * A request for one product gives a one-item list. Only a hint — the AI also reads the request itself,
 * so a single product whose name contains "and" / "&" ("Salt and Pepper") is still found as one product.
 */
export const productNamesFrom = (prompt) => {
  const whole = productNameFrom(prompt);
  const seen = new Set();
  const parts = whole
    .split(/\s*(?:,|;|&|\+|\n|\band\b)\s*/i)
    .map((p) => p.trim())
    .filter((p) => nameTokens(p).length > 0 && !seen.has(p.toLowerCase()) && seen.add(p.toLowerCase()));
  return parts.length > 1 ? parts : [whole];
};

const nameTokens = (name) =>
  String(name || "")
    .toLowerCase()
    .split(/[^a-z0-9\u00c0-\uffff]+/)
    .filter((t) => t.length >= 2 && !STOP_WORDS.has(t));

// Whole words only ("apple" must not match "pineapple"); plurals and small suffixes still match.
const itemMatchesToken = (str, token) =>
  str
    .toLowerCase()
    .split(/[^a-z0-9\u00c0-\uffff]+/)
    .some((w) => w === token || (w.length >= 3 && token.length >= 3 && (w.startsWith(token) || (w.length >= 4 && token.startsWith(w)))));

const NAME_RADIUS = 0.35; // how far (fraction of the page) from a picture its caption may be

/**
 * How well the product name is printed around a picture, from the page text.
 * score = share of the name's words found within NAME_RADIUS of the picture (1 = all of them);
 * pageScore = same, anywhere on the page; dist = distance to the nearest matching word.
 */
const nameMatch = (items, viewport, p, tokens) => {
  if (!tokens.length) return { score: 0, pageScore: 0, dist: 1 };
  // A caption belongs to a small picture only when it is close to it; big pictures keep the full radius.
  const radius = Math.min(NAME_RADIUS, Math.max(0.08, 2.5 * Math.max(p.w, p.h)));
  const near = new Set();
  const page = new Set();
  let dist = 1;
  for (const it of items) {
    if (!it.str || !it.str.trim() || !it.transform) continue;
    const hit = tokens.filter((t) => itemMatchesToken(it.str, t));
    if (!hit.length) continue;
    hit.forEach((t) => page.add(t));
    const [vx, vy] = viewport.convertToViewportPoint(it.transform[4], it.transform[5]);
    const x = vx / viewport.width;
    const y = vy / viewport.height;
    const dx = Math.max(p.x - x, 0, x - (p.x + p.w));
    const dy = Math.max(p.y - y, 0, y - (p.y + p.h));
    const d = Math.hypot(dx, dy);
    dist = Math.min(dist, d);
    if (d <= radius) hit.forEach((t) => near.add(t));
  }
  return { score: near.size / tokens.length, pageScore: page.size / tokens.length, dist };
};

/** Text printed around an image (product names are usually in a caption next to the photo). */
const nearbyText = (items, viewport, p) => {
  const hits = [];
  for (const it of items) {
    if (!it.str || !it.str.trim() || !it.transform) continue;
    const [vx, vy] = viewport.convertToViewportPoint(it.transform[4], it.transform[5]);
    const x = vx / viewport.width;
    const y = vy / viewport.height;
    if (x >= p.x - 0.05 && x <= p.x + p.w + 0.05 && y >= p.y - 0.04 && y <= p.y + p.h + 0.15) hits.push({ x, y, s: it.str.trim() });
  }
  hits.sort((a, b) => (Math.abs(a.y - b.y) < 0.008 ? a.x - b.x : a.y - b.y));
  return hits.map((h) => h.s).join(" ").replace(/\s+/g, " ").slice(0, 200);
};

/**
 * A page backdrop is not a product photo: a very large picture that other pictures sit on top of, or a
 * near-full-page picture on a page that also has smaller ones. Flags them so they are never offered as a
 * product (their caption text overlaps them, which made them look like the best "name" match).
 */
const flagBackdrops = (found) => {
  const inside = (o, c) => {
    const w = Math.max(0, Math.min(o.x + o.w, c.x + c.w) - Math.max(o.x, c.x));
    const h = Math.max(0, Math.min(o.y + o.h, c.y + c.h) - Math.max(o.y, c.y));
    return o.w * o.h > 0 && (w * h) / (o.w * o.h) >= 0.8;
  };
  found.forEach((c) => {
    const others = found.filter((o) => o !== c && o.pageNumber === c.pageNumber);
    c.backdrop =
      others.some((o) => o.area < c.area * 0.5 && inside(o.placement, c.placement)) || // smaller pictures sit on top of it
      (c.area > 0.6 && others.length > 0); // near full page, with real pictures elsewhere on the page
  });
};

/**
 * Lists the separate pictures placed in the PDF and prepares small previews for the AI.
 * @returns {{ candidates: object[], hasPageSizedImages: boolean }}
 *   candidates: [{ id, page(pdf.js page), pageNumber, placement, thumb(Blob), text }]
 */
export const collectCandidates = async (file, { onProgress, products = [] } = {}) => {
  const { pdfjsLib, pdf } = await loadPdf(file);
  const found = [];
  const seen = new Set();
  let hasPageSizedImages = false;

  for (let n = 1; n <= pdf.numPages; n++) {
    const page = await pdf.getPage(n);
    const placements = await collectImagePlacements(pdfjsLib, page);
    placements.forEach((placement, k) => {
      const area = placement.w * placement.h;
      if (area > MAX_AREA) hasPageSizedImages = true;
      if (area < MIN_AREA || Math.min(placement.w, placement.h) < MIN_SIDE || area > MAX_AREA) return;
      const key = placement.id ? `${placement.id}` : `inline-${n}-${k}`;
      if (placement.id && seen.has(`${n}:${key}`)) return; // same picture painted twice on a page
      seen.add(`${n}:${key}`);
      found.push({ page, pageNumber: n, placement, area });
    });
    onProgress?.(n, pdf.numPages);
  }

  flagBackdrops(found);
  const backdrops = found.filter((c) => c.backdrop);
  // Backdrops are left out; the page scan can still look at those pages if nothing else matches.
  if (backdrops.length) hasPageSizedImages = true;
  const textCache = new Map();
  const getText = async (c) => {
    if (!textCache.has(c.pageNumber)) {
      try {
        textCache.set(c.pageNumber, (await c.page.getTextContent()).items);
      } catch {
        textCache.set(c.pageNumber, []);
      }
    }
    return textCache.get(c.pageNumber);
  };
  // Only MAX_CANDIDATES pictures can be sent. A busy flyer has more, so the pictures with a requested product's
  // name printed right beside them go first; the rest are filled in by size.
  const usable = found.filter((c) => !c.backdrop);
  if (usable.length > MAX_CANDIDATES && products.length) {
    const tokenSets = products.map(nameTokens);
    await Promise.all(
      usable.map(async (c) => {
        const items = await getText(c);
        const viewport = c.page.getViewport({ scale: 1 });
        c.nameRank = tokenSets.reduce(
          (best, tokens) => {
            const m = nameMatch(items, viewport, c.placement, tokens);
            return m.score > best.score || (m.score === best.score && m.dist < best.dist) ? m : best;
          },
          { score: 0, dist: 1 }
        );
      })
    );
    usable.sort((a, b) => b.nameRank.score - a.nameRank.score || (b.nameRank.score > 0 ? a.nameRank.dist - b.nameRank.dist : 0) || b.area - a.area);
  } else {
    usable.sort((a, b) => b.area - a.area);
  }
  const chosen = usable.slice(0, MAX_CANDIDATES);

  // Decode previews a few at a time to keep memory and time down.
  const candidates = [];
  for (let i = 0; i < chosen.length; i += 4) {
    const batch = await Promise.all(
      chosen.slice(i, i + 4).map(async (c, j) => {
        try {
          const canvas = await decodeCandidate(c);
          if (!canvas) return null;
          const thumb = await makeThumb(canvas);
          canvas.width = canvas.height = 0;
          if (!thumb) return null;
          const items = await getText(c);
          const viewport = c.page.getViewport({ scale: 1 });
          const printed = nearbyText(items, viewport, c.placement);
          // How well each requested product's name is printed around this picture (computed on demand).
          const nameFor = (product) => nameMatch(items, viewport, c.placement, nameTokens(product));
          const text = printed;
          return { id: `c${i + j + 1}`, page: c.page, pageNumber: c.pageNumber, placement: c.placement, thumb, text, printed, nameFor, area: c.area };
        } catch {
          return null;
        }
      })
    );
    candidates.push(...batch.filter(Boolean));
  }
  // Tell the AI which requested product names are printed right by a picture - only for the few pictures closest to
  // each name (on a busy flyer a caption is within reach of many pictures, and tagging all of them would mislead it).
  const tagged = new Map();
  products.forEach((product) => {
    candidates
      .map((c) => ({ c, m: c.nameFor(product) }))
      .filter(({ m }) => m.score === 1)
      .sort((a, b) => a.m.dist - b.m.dist)
      .slice(0, 3)
      .forEach(({ c }) => tagged.set(c.id, [...(tagged.get(c.id) || []), product]));
  });
  candidates.forEach((c) => {
    const near = tagged.get(c.id);
    if (near) c.text = `[the product name ${near.map((n) => `"${n}"`).join(" / ")} is printed near this image] ${c.printed}`.slice(0, 280);
  });
  return { candidates, hasPageSizedImages };
};

/**
 * The real embedded picture, untouched, as a PNG Blob at its native pixel size.
 * @returns {Promise<{ blob: Blob, width: number, height: number } | null>}
 */
export const getExactImage = async (candidate) => {
  const canvas = await decodeCandidate(candidate);
  if (!canvas) return null;
  const { width, height } = canvas;
  const blob = await toBlob(canvas, "image/png");
  canvas.width = canvas.height = 0;
  return blob ? { blob, width, height } : null;
};

/* ------------------------------------------------------------------ */
/* Whole flow (used by the Image Extraction card and the Workflow runner) */
/* ------------------------------------------------------------------ */

/**
 * PDF + "which product" request → enhanced product picture(s).
 * Fast path: the picture is a separate image inside the PDF; the AI only sees small
 * previews and the real embedded image is enhanced. Fallback for scanned/flattened PDFs:
 * find the product on the page image and crop it.
 *
 * @param {{ file: File, prompt: string, onStatus?: (s: string) => void }} args
 * @returns {Promise<{ results: object[], message: string }>}
 *   result = { page, label, confidence, exact: {url,width,height}|null, originalDataUrl,
 *              enhancedDataUrl, backgroundReplaced, error }
 *   exact.url is an object URL: the caller revokes it (URL.revokeObjectURL) when done.
 */
export const runImageExtraction = async ({ file, prompt, single = false, products: namedProducts = null, onStatus = () => {} }) => {
  onStatus("Scanning the PDF…");
  // The products the customer named (one or several). `single`: the caller knows it is exactly one product.
  // `namedProducts`: the caller already has the exact list (e.g. several menu items sent together), so names
  // containing "and" / "&" are kept whole instead of being split.
  const exactList = Array.isArray(namedProducts) && namedProducts.length > 0;
  const products = exactList ? namedProducts : single ? [productNameFrom(prompt)] : productNamesFrom(prompt);
  const { candidates, hasPageSizedImages } = await collectCandidates(file, { products });

  let found = [];
  let note = "";
  let requested = products;
  let usePageFallback = candidates.length === 0;

  if (!usePageFallback) {
    onStatus(products.length > 1 ? "Finding the products…" : "Finding the product…");
    const located = await locateImageCandidates({
      thumbs: candidates.map((c) => c.thumb),
      meta: candidates.map((c) => ({ id: c.id, text: c.text })),
      prompt,
      single,
    });
    // The AI's reading of the request is authoritative; our own split is the fallback.
    requested = exactList ? products : located.items.length ? located.items : products;
    const seenIds = new Set();
    let matches = located.matches
      .filter((m) => !seenIds.has(m.id) && seenIds.add(m.id)) // one picture is never given to two products
      .map((m) => ({ ...m, item: m.item || (requested.length === 1 ? requested[0] : m.label) }));

    // The AI wasn't sure about a product, but its name is printed in the PDF: take the picture next to it.
    // Page backdrops were already removed, so this can't pick a background.
    const byName = (name) => {
      const pick = (key) =>
        candidates
          .filter((c) => !seenIds.has(c.id) && c.nameFor(name)[key] >= 1)
          .filter((c) => key === "score" || candidates.filter((x) => x.pageNumber === c.pageNumber).length === 1) // name only on the page: only if that page has just one picture
          .sort((a, b) => a.nameFor(name).dist - b.nameFor(name).dist || a.area - b.area)
          .slice(0, 1); // only the picture nearest the name — a second one could be a neighbouring product
      return pick("score").length ? pick("score") : pick("pageScore");
    };
    const missingNow = () => requested.filter((r) => !matches.some((m) => m.item.toLowerCase() === r.toLowerCase()));
    for (const name of missingNow()) {
      const hit = byName(name)[0];
      if (!hit) continue;
      seenIds.add(hit.id);
      matches.push({ id: hit.id, item: name, label: name, confidence: 0.5 });
    }
    // One product whose name contains "and" / "&" ("Salt and Pepper") may have been split into two above:
    // when nothing was found, try the whole name as a single product.
    if (matches.length === 0 && products.length > 1 && !exactList) {
      const whole = productNameFrom(prompt);
      const hit = byName(whole)[0];
      if (hit) {
        matches.push({ id: hit.id, item: whole, label: whole, confidence: 0.5 });
        requested = [whole];
      }
    }

    // Second pass. When several products were named, the AI can miss some of them (its attention is split
    // across the names and the whole picture set). Ask again about each missing product on its own, looking
    // only at the pictures that have not been given to another product yet.
    if (requested.length > 1) {
      const sameName = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();
      const missingNames = requested.filter((r) => !matches.some((m) => sameName(m.item, r)));
      const pool = candidates.filter((c) => !seenIds.has(c.id));
      if (missingNames.length && pool.length) {
        onStatus(missingNames.length > 1 ? "Looking again for the remaining products…" : "Looking again for the remaining product…");
        const again = await Promise.all(
          missingNames.map((name) =>
            locateImageCandidates({
              thumbs: pool.map((c) => c.thumb),
              meta: pool.map((c) => ({ id: c.id, text: c.text })),
              prompt: name,
              single: true,
            }).then(
              (r) => ({ name, found: r.matches || [] }),
              () => ({ name, found: [] }) // one failed search must not lose the others
            )
          )
        );
        for (const { name, found: hits } of again) {
          const hit = hits.find((h) => !seenIds.has(h.id)); // best first; never reuse a picture
          if (!hit) continue;
          seenIds.add(hit.id);
          matches.push({ id: hit.id, item: name, label: hit.label || name, confidence: hit.confidence });
        }
      }
    }

    // Verify before extracting. The AI chose each picture from small previews, so look again at a larger size and
    // check it really is the product that was named, and the only product shown. A wrong picture is dropped; the
    // product then gets its next-best candidates (pictures with its name printed beside them first), each checked
    // the same way. If the check itself can't run, the picture is kept and marked "unchecked".
    const sameItem = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();
    const byId = (id) => candidates.find((c) => c.id === id);
    const imageCache = new Map(); // candidate id → Promise<{ exact, check } | null>: each PDF picture is decoded once
    const imagesOf = (c) => {
      if (!imageCache.has(c.id)) {
        imageCache.set(
          c.id,
          (async () => {
            const canvas = await decodeCandidate(c);
            if (!canvas) return null;
            const exact = { blob: await toBlob(canvas, "image/png"), width: canvas.width, height: canvas.height };
            const check = await makeVerifyBlob(canvas);
            canvas.width = canvas.height = 0;
            return exact.blob && check ? { exact, check } : null;
          })().catch(() => null)
        );
      }
      return imageCache.get(c.id);
    };
    const verifyPicture = async (c, name) => {
      try {
        const imgs = await imagesOf(c);
        if (!imgs) return { status: "unreadable" };
        return await verifyExtractedImage({ blob: imgs.check, name, text: c.text });
      } catch {
        return { status: "unchecked" }; // the check couldn't run: keep the picture, flagged
      }
    };
    const refused = new Map(); // product (lower case) → ids of pictures that were checked and refused
    const refuse = (item, id) => refused.set(String(item).toLowerCase(), (refused.get(String(item).toLowerCase()) || new Set()).add(id));
    const rejected = (v) => v.status === "rejected" || v.status === "unreadable";

    if (matches.length) {
      onStatus(matches.length > 1 ? "Checking the pictures…" : "Checking the picture…");
      const checked = await Promise.all(matches.map(async (m) => ({ m, v: await verifyPicture(byId(m.id), m.item) })));
      matches = [];
      for (const { m, v } of checked) {
        if (rejected(v)) refuse(m.item, m.id);
        else matches.push({ ...m, verified: v.status, shows: v.shows || "" });
      }
    }
    const wrongPicks = [...refused.values()].reduce((n, set) => n + set.size, 0);

    // Products whose pick was refused (or never found, but whose name is printed beside a picture): try the next-best
    // pictures, one verified at a time. Pictures already given to another product are never reused.
    const needMore = requested.filter((r) => !matches.some((m) => sameItem(m.item, r)));
    if (needMore.length && candidates.length) {
      onStatus(needMore.length > 1 ? "Looking for the right pictures…" : "Looking for the right picture…");
      const claimed = new Set(matches.map((m) => m.id));
      await Promise.all(
        needMore.map(async (name) => {
          const tried = refused.get(name.toLowerCase()) || new Set();
          const hadWrongPick = tried.size > 0;
          const options = candidates
            .filter((c) => !claimed.has(c.id) && !tried.has(c.id))
            .map((c) => ({ c, n: c.nameFor(name) }))
            .filter(({ n }) => hadWrongPick || n.score > 0 || n.pageScore >= 1) // never matched: only pictures whose caption names the product
            .sort((a, b) => b.n.score - a.n.score || b.n.pageScore - a.n.pageScore || a.n.dist - b.n.dist || a.c.area - b.c.area)
            .slice(0, MAX_ALTERNATES);
          for (const { c } of options) {
            if (claimed.has(c.id)) continue; // another product took it meanwhile
            claimed.add(c.id);
            const v = await verifyPicture(c, name);
            if (rejected(v)) {
              claimed.delete(c.id);
              refuse(name, c.id);
              continue;
            }
            matches.push({ id: c.id, item: name, label: name, confidence: 0.5, verified: v.status, shows: v.shows || "" });
            return;
          }
        })
      );
    }
    // A product that has a verified picture doesn't also need its "couldn't fully confirm" ones.
    const hasVerified = new Set(matches.filter((m) => m.verified === "verified").map((m) => String(m.item).toLowerCase()));
    matches = matches.filter((m) => m.verified !== "unsure" || !hasVerified.has(String(m.item).toLowerCase()));

    if (matches.length === 0) {
      // Only worth a slow page scan when the PDF has scanned/flattened pages the previews can't cover.
      usePageFallback = hasPageSizedImages;
      note = wrongPicks
        ? "The pictures found in that PDF didn't match the product you named, so none were extracted."
        : "No matching product image was found in that PDF.";
    } else {
      onStatus("Enhancing the image…");
      found = await Promise.all(
        matches.map(async (m) => {
          const c = candidates.find((x) => x.id === m.id);
          // label = the product name the customer asked for (what the picture is for), not the AI's description.
          const base = { page: c.pageNumber, item: m.item, label: m.item || m.label, printedLabel: m.label, confidence: m.confidence, verified: m.verified || "unchecked", shows: m.shows || "", exact: null, originalDataUrl: null, enhancedDataUrl: null, backgroundReplaced: false, fallback: false, error: null };
          try {
            const exactImg = (await imagesOf(c))?.exact;
            if (!exactImg) throw new Error("Couldn't read that image from the PDF.");
            base.exact = { url: URL.createObjectURL(exactImg.blob), width: exactImg.width, height: exactImg.height };
            const enhanced = await enhanceExtractedImage(exactImg.blob);
            base.enhancedDataUrl = enhanced.enhancedDataUrl;
            base.backgroundReplaced = !!enhanced.backgroundReplaced;
            base.fallback = !!enhanced.fallback;
          } catch (err) {
            base.error = err.message || "Couldn't enhance this image.";
          }
          return base;
        })
      );
      // Keep the customer's order: the first product they named comes first.
      const order = (r) => {
        const i = requested.findIndex((n) => n.toLowerCase() === String(r.item || "").toLowerCase());
        return i === -1 ? requested.length : i;
      };
      found.sort((a, b) => order(a) - order(b));

      // Third pass. Some named products are still missing and the PDF has page-sized (scanned / flattened)
      // pictures the previews can't cover: scan the pages for just those products and add them to the result.
      const sameName = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();
      const stillMissing = requested.filter((r) => !found.some((f) => sameName(f.item, r)));
      if (stillMissing.length && hasPageSizedImages) {
        try {
          onStatus(stillMissing.length > 1 ? "Looking for the remaining products on the pages…" : "Looking for the remaining product on the page…");
          const pages = await renderPdfPages(file, { onProgress: (done, total) => onStatus(`Reading PDF pages… ${done}/${total}`) });
          if (pages.length) {
            const res = await extractProductImages({
              pages,
              prompt: `Extract product image for ${stillMissing.join("; ")}`,
              single: stillMissing.length === 1,
            });
            const extra = (res.results || [])
              .map((r) => (stillMissing.length === 1 ? { ...r, item: stillMissing[0], label: stillMissing[0] } : r))
              .filter((r) => stillMissing.some((n) => sameName(n, r.item)));
            found = [...found, ...extra];
            found.sort((a, b) => order(a) - order(b));
          }
        } catch {
          // The page scan is a bonus: the pictures already found are still returned.
        }
      }
    }
  }

  if (usePageFallback) {
    onStatus("This PDF has no separate pictures — reading pages…");
    const pages = await renderPdfPages(file, { onProgress: (done, total) => onStatus(`Reading PDF pages… ${done}/${total}`) });
    if (!pages.length) throw new Error("Couldn't read any pages from that PDF.");
    onStatus(products.length > 1 ? "Finding the products on the page…" : "Finding the product on the page…");
    const res = await extractProductImages({ pages, prompt, single });
    found = res.results;
    if (res.items?.length) requested = res.items;
    note = res.message || note;
  }

  // Products the customer named that no picture was found for.
  const missing = found.length
    ? requested.filter((r) => !found.some((f) => String(f.item || "").toLowerCase() === r.toLowerCase()))
    : [];
  return { results: found, message: note, requested, missing };
};

/* ------------------------------------------------------------------ */
/* Extract every embedded picture (no AI) — "Extract All Images"        */
/* ------------------------------------------------------------------ */

const EXPORT_MIN_SIDE_PX = 16; // a picture with a side under this many real pixels is a dot or hairline, not an image
const READING_ROW = 0.02; // pictures whose tops are within 2% of the page height read as one row (left to right)

/**
 * Ids of pictures painted only inside a soft-mask group. A PDF uses those black/white shapes internally to cut out
 * artwork (they are not images a customer wants). A picture that is also painted as normal artwork is not listed.
 */
const findMaskOnlyImageIds = async (pdfjsLib, page) => {
  const { OPS } = pdfjsLib;
  const list = await page.getOperatorList();
  const groups = []; // one entry per open group: true when it is a soft-mask group
  const inMask = new Set();
  const outside = new Set();
  for (let i = 0; i < list.fnArray.length; i++) {
    const fn = list.fnArray[i];
    if (fn === OPS.beginGroup) groups.push(!!list.argsArray[i]?.[0]?.smask);
    else if (fn === OPS.endGroup) groups.pop();
    else if (fn === OPS.paintImageXObject || fn === OPS.paintJpegXObject) {
      (groups.some(Boolean) ? inMask : outside).add(list.argsArray[i][0]);
    }
  }
  return new Set([...inMask].filter((id) => !outside.has(id)));
};

/**
 * Every separate picture embedded in the PDF, each as its own PNG at its native pixel size.
 * A picture painted several times (same embedded image) is returned once; the PDF's internal soft-mask shapes are left out. Pictures come back in reading order
 * (page by page, top to bottom, left to right) and are named page-<n>_image-<nn>.png.
 *
 * @param {File} file the PDF
 * @param {{ onProgress?: (p: { page: number, pages: number, images: number }) => void, withThumbs?: boolean }} [opts]
 *   withThumbs adds a small JPEG preview (`thumb`) to every image, for the AI.
 * @returns {Promise<{ images: { name: string, pageNumber: number, width: number, height: number, blob: Blob }[], skipped: number }>}
 *   skipped = pictures that could not be decoded (e.g. 1-bit masks)
 */
export const extractAllEmbeddedImages = async (file, { onProgress, withThumbs = false } = {}) => {
  const { pdfjsLib, pdf } = await loadPdf(file);
  const images = [];
  const seenShared = new Set(); // pictures shared across pages ("g_" objects) are returned once
  let skipped = 0;

  for (let n = 1; n <= pdf.numPages; n++) {
    const page = await pdf.getPage(n);
    const placements = await collectImagePlacements(pdfjsLib, page);
    const maskOnly = await findMaskOnlyImageIds(pdfjsLib, page);
    const seenOnPage = new Set();
    const unique = [];
    placements.forEach((placement) => {
      if (placement.id && maskOnly.has(placement.id)) return; // an internal cut-out shape, not an image
      if (placement.id) {
        const shared = String(placement.id).startsWith("g_");
        if (shared ? seenShared.has(placement.id) : seenOnPage.has(placement.id)) return;
        (shared ? seenShared : seenOnPage).add(placement.id);
      }
      unique.push(placement);
    });
    unique.sort((a, b) => Math.round(a.y / READING_ROW) - Math.round(b.y / READING_ROW) || a.x - b.x);

    let indexOnPage = 0;
    for (const placement of unique) {
      try {
        const canvas = await decodeCandidate({ page, placement });
        if (!canvas) {
          skipped++;
          continue;
        }
        const { width, height } = canvas;
        if (Math.min(width, height) < EXPORT_MIN_SIDE_PX) {
          canvas.width = canvas.height = 0;
          continue;
        }
        const blob = await toBlob(canvas, "image/png");
        const thumb = withThumbs ? await makeThumb(canvas) : null;
        canvas.width = canvas.height = 0; // release memory
        if (!blob) {
          skipped++;
          continue;
        }
        indexOnPage++;
        images.push({ name: `page-${n}_image-${String(indexOnPage).padStart(2, "0")}.png`, pageNumber: n, width, height, blob, ...(thumb ? { thumb } : {}) });
      } catch {
        skipped++; // one unreadable picture must not lose the others
      }
    }
    try {
      page.cleanup();
    } catch {
      /* freeing page memory is best-effort */
    }
    onProgress?.({ page: n, pages: pdf.numPages, images: images.length });
  }
  return { images, skipped };
};

/* ------------------------------------------------------------------ */
/* Extract every PRODUCT picture (AI keeps product photos only)         */
/* ------------------------------------------------------------------ */

const PRODUCT_MIN_SIDE_PX = 32; // a picture thinner than this can't be a product photo
const CLASSIFY_CHUNK = 30; // pictures per AI request
const CLASSIFY_PARALLEL = 3; // requests at the same time

/** Runs worker(item) for every item, a few at a time. */
const runPool = async (items, size, worker) => {
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(size, items.length) }, async () => {
      while (next < items.length) {
        const mine = next++;
        await worker(items[mine], mine);
      }
    })
  );
};

/**
 * Every product picture in the PDF, each as its own PNG at its native size. All embedded pictures are pulled out,
 * then the AI keeps the product photos and drops logos, banners, QR codes, badges, store photos and the like.
 * Files are named after the product when its label is readable (heinz-tomato-ketchup.png).
 *
 * @param {File} file the PDF
 * @param {{ onStatus?: (s: string) => void }} [opts]
 * @returns {Promise<{ products: { name: string, label: string, pageNumber: number, width: number, height: number, blob: Blob }[],
 *   scanned: number, unchecked: number }>}  unchecked = pictures that couldn't be checked (their AI request failed)
 */
export const extractAllProductImages = async (file, { onStatus = () => {} } = {}) => {
  onStatus("Scanning the PDF…");
  const { images } = await extractAllEmbeddedImages(file, {
    withThumbs: true,
    onProgress: ({ page, pages, images: found }) => onStatus(pages > 1 ? `Scanning the PDF… page ${page}/${pages} (${found} pictures)` : `Scanning the PDF… ${found} pictures`),
  });
  const candidates = images.map((img, i) => ({ ...img, id: `i${i + 1}` })).filter((c) => c.thumb && Math.min(c.width, c.height) >= PRODUCT_MIN_SIDE_PX);
  if (!candidates.length) return { products: [], scanned: images.length, unchecked: 0 };

  const chunks = [];
  for (let i = 0; i < candidates.length; i += CLASSIFY_CHUNK) chunks.push(candidates.slice(i, i + CLASSIFY_CHUNK));
  const labels = new Map(); // id -> label of every picture the AI kept
  let unchecked = 0;
  let doneChunks = 0;
  let lastError = null;
  onStatus(`Finding the products among ${candidates.length} pictures…`);
  await runPool(chunks, CLASSIFY_PARALLEL, async (chunk) => {
    try {
      const { products } = await classifyProductImages({ thumbs: chunk.map((c) => c.thumb), meta: chunk.map((c) => ({ id: c.id })) });
      products.forEach((p) => labels.set(p.id, p.label || ""));
    } catch (err) {
      lastError = err; // one failed request must not lose the other pictures
      unchecked += chunk.length;
    }
    doneChunks++;
    onStatus(`Finding the products… ${Math.min(doneChunks * CLASSIFY_CHUNK, candidates.length)}/${candidates.length}`);
  });
  if (unchecked === candidates.length) throw lastError || new Error("Couldn't check the pictures. Please try again.");

  const used = new Map(); // file name -> times used, so two "Crisco Oil" pictures don't overwrite each other
  const products = candidates
    .filter((c) => labels.has(c.id))
    .map((c, i) => {
      const label = labels.get(c.id) || `Product ${i + 1}`;
      const base = slugify(label) || `product-${String(i + 1).padStart(2, "0")}`;
      const count = (used.get(base) || 0) + 1;
      used.set(base, count);
      return { name: `${base}${count > 1 ? `-${count}` : ""}.png`, label, pageNumber: c.pageNumber, width: c.width, height: c.height, blob: c.blob };
    });
  return { products, scanned: images.length, unchecked };
};
